import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { ChevronLeft } from "lucide-react";

export default function Terms() {
  return (
    <div className="min-h-screen bg-background py-10">
      <div className="container mx-auto px-4">
        <div className="mb-8">
          <Link href="/">
            <Button variant="ghost" className="flex items-center gap-1 hover:bg-transparent hover:text-primary p-0">
              <ChevronLeft className="h-4 w-4" />
              <span>Back to Home</span>
            </Button>
          </Link>
        </div>
        
        <div className="max-w-3xl mx-auto bg-card p-8 rounded-lg shadow-sm">
          <h1 className="text-3xl font-bold mb-6">Terms of Service</h1>
          
          <div className="prose dark:prose-invert max-w-none">
            <p className="mb-4">
              Welcome to NoLicense Nearby. These Terms of Service govern your use of our website and music licensing services.
              By accessing or using our platform, you agree to be bound by these Terms.
            </p>
            
            <h2 className="text-xl font-semibold mt-6 mb-3">1. Licensing Agreement</h2>
            <p className="mb-4">
              NoLicense Nearby grants you a non-exclusive license to use the music tracks for the specific purposes 
              outlined in your license request and approved by our platform. Each license is specific to the project 
              and usage duration specified in your request.
            </p>
            
            <h2 className="text-xl font-semibold mt-6 mb-3">2. License Types and Restrictions</h2>
            <p className="mb-4">
              We offer licenses for various uses including:
            </p>
            <ul className="list-disc pl-6 mb-4">
              <li><strong>Social Media:</strong> For use in social media videos, content creation, and influencer marketing</li>
              <li><strong>TV Production:</strong> For television shows, commercials, and broadcasts</li>
              <li><strong>Film:</strong> For feature films, short films, and documentaries</li>
              <li><strong>Corporate:</strong> For internal videos, presentations, and corporate communications</li>
            </ul>
            
            <p className="mb-4">
              All licenses are subject to the following restrictions:
            </p>
            <ul className="list-disc pl-6 mb-4">
              <li>You may not redistribute, resell, or sublicense the music tracks</li>
              <li>You may not claim ownership or copyright of the music</li>
              <li>You may not use the music in projects that contain explicit content without prior approval</li>
              <li>You may not use the music for political campaigns without explicit permission</li>
            </ul>
            
            <h2 className="text-xl font-semibold mt-6 mb-3">3. License Duration</h2>
            <p className="mb-4">
              Licenses are granted for the duration specified in your approved license request, which may be:
            </p>
            <ul className="list-disc pl-6 mb-4">
              <li>6 months</li>
              <li>1 year</li>
              <li>3 years</li>
              <li>Perpetual (lifetime)</li>
            </ul>
            
            <h2 className="text-xl font-semibold mt-6 mb-3">4. Payments and Refunds</h2>
            <p className="mb-4">
              All payments for licenses are processed through Stripe. Prices are determined based on the license type, 
              usage duration, and number of tracks. Once a license is purchased and the music is delivered, we do not 
              offer refunds unless there is a technical issue with the music files.
            </p>
            
            <h2 className="text-xl font-semibold mt-6 mb-3">5. Referral Program</h2>
            <p className="mb-4">
              Our referral program allows users to earn commissions for referring new customers. Referral commissions 
              are paid after the referred customer completes their first purchase. All referral activities must comply 
              with our terms and should not involve spam or misleading practices.
            </p>
            
            <h2 className="text-xl font-semibold mt-6 mb-3">6. Intellectual Property</h2>
            <p className="mb-4">
              All music tracks remain the intellectual property of NoBanks Nearby. You are purchasing a license to use 
              the music, not ownership of the tracks. Proper credit should be given where possible, but is not required 
              unless specified in your license agreement.
            </p>
            
            <h2 className="text-xl font-semibold mt-6 mb-3">7. Termination</h2>
            <p className="mb-4">
              We reserve the right to terminate or suspend your access to our platform if you violate these Terms. 
              Upon termination, your license rights will continue for any previously purchased and approved licenses, 
              but you will not be able to purchase new licenses.
            </p>
            
            <h2 className="text-xl font-semibold mt-6 mb-3">8. Changes to Terms</h2>
            <p className="mb-4">
              We may update these Terms from time to time. The updated version will be indicated by an updated 
              "Last Updated" date at the top of this page. Your continued use of the platform after changes are 
              made constitutes acceptance of the updated Terms.
            </p>
            
            <p className="mt-8 text-sm text-muted-foreground">
              Last Updated: March 25, 2025
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}